// zipScanner — extract a file-path list from an in-memory .zip blob.
//
// Used by the "Upload repository" flow. Pure utility — no React, no I/O
// beyond reading the supplied File/Blob. Caller is responsible for
// providing a size-validated file. Returns sorted unique POSIX paths.
//
// Filtering policy mirrors what a developer would expect from an
// architecture scan:
//   - Strip the single top-level folder (`my-repo-main/`) if every
//     entry shares one — GitHub zips wrap content in a folder.
//   - Skip vendored / generated trees (node_modules, .git, dist, build,
//     .next, .turbo, coverage, .cache, vendor, target, __pycache__).
//   - Skip binary file extensions (images, fonts, archives, media).
//   - Cap total file count at MAX_FILES to keep cartographer rendering
//     responsive.

import JSZip from 'jszip';

export interface ZipScanOptions {
  /** Hard cap on retained file paths. Default 4000. */
  maxFiles?: number;
  /** Optional set of allowed extensions (without dot). Default = text-like. */
  allowedExts?: ReadonlySet<string>;
}

export interface ZipScanResult {
  /** Sorted, unique POSIX file paths after filtering + root-strip. */
  files: string[];
  /** Number of zip entries discarded by filters. */
  skipped: number;
  /** Detected stripped root prefix, if any. */
  rootPrefix: string | null;
}

// Sensible default for arch-mapping: code, config, docs, prose.
const DEFAULT_ALLOWED_EXTS = new Set<string>([
  // code
  'ts', 'tsx', 'js', 'jsx', 'mjs', 'cjs',
  'py', 'go', 'rs', 'java', 'kt', 'rb', 'php', 'cs', 'swift', 'm', 'mm',
  'c', 'cc', 'cpp', 'h', 'hpp', 'scala', 'clj', 'ex', 'exs', 'elm',
  'lua', 'dart', 'sql', 'r',
  // web
  'html', 'htm', 'css', 'scss', 'sass', 'less', 'vue', 'svelte', 'astro',
  // shell
  'sh', 'bash', 'zsh', 'fish', 'ps1', 'bat', 'cmd',
  // config / data
  'json', 'yml', 'yaml', 'toml', 'ini', 'env', 'xml', 'csv', 'tsv',
  // docs / prose
  'md', 'mdx', 'rst', 'txt', 'adoc',
  // office (the user explicitly wanted these)
  'doc', 'docx',
  // dockerfile / makefile have no ext — handled separately below.
]);

const SPECIAL_BASENAMES = new Set<string>([
  'dockerfile', 'makefile', 'rakefile', 'gemfile', 'procfile',
  'readme', 'license', 'changelog', 'contributing', 'authors',
  'notice', 'codeowners', '.gitignore', '.dockerignore',
]);

const SKIP_DIR_RE = new RegExp(
  '(^|/)(' +
  [
    'node_modules', '\\.git', 'dist', 'build', '\\.next', '\\.turbo',
    'coverage', '\\.cache', 'vendor', 'target', '__pycache__', '\\.venv',
    'venv', '\\.idea', '\\.vscode', 'out', '\\.parcel-cache', '\\.nx',
    '\\.expo', '\\.gradle', 'tmp', 'temp',
  ].join('|') +
  ')(/|$)',
);

const SKIP_FILE_RE = /\.(lock|min\.js|min\.css|map|woff2?|ttf|otf|eot|png|jpe?g|gif|webp|svg|ico|avif|mp4|webm|mov|mp3|wav|ogg|zip|tar|gz|bz2|7z|rar|pdf|class|jar|so|dll|exe|bin|wasm)$/i;

const LOCKFILE_BASENAMES = new Set<string>([
  'package-lock.json', 'yarn.lock', 'pnpm-lock.yaml', 'bun.lockb',
  'composer.lock', 'cargo.lock', 'gemfile.lock', 'poetry.lock',
  'pipfile.lock', 'go.sum',
]);

function detectRootPrefix(paths: string[]): string | null {
  if (paths.length === 0) return null;
  const first = paths[0].split('/')[0];
  if (!first) return null;
  for (const p of paths) {
    if (!p.startsWith(first + '/') && p !== first) return null;
  }
  return first + '/';
}

function isAllowed(path: string, allowedExts: ReadonlySet<string>): boolean {
  const base = path.split('/').pop()?.toLowerCase() ?? '';
  if (!base) return false;
  if (LOCKFILE_BASENAMES.has(base)) return false;
  if (SKIP_FILE_RE.test(base)) return false;
  if (SPECIAL_BASENAMES.has(base)) return true;
  // basename without extension hits (e.g. "Dockerfile.prod" — keep)
  for (const sp of SPECIAL_BASENAMES) {
    if (base.startsWith(sp + '.')) return true;
  }
  const dot = base.lastIndexOf('.');
  if (dot <= 0) return false;
  const ext = base.slice(dot + 1);
  return allowedExts.has(ext);
}

/**
 * Read a .zip blob and return the filtered file-path list.
 */
export async function scanZip(
  blob: Blob,
  options: ZipScanOptions = {},
): Promise<ZipScanResult> {
  const maxFiles = options.maxFiles ?? 4000;
  const allowedExts = options.allowedExts ?? DEFAULT_ALLOWED_EXTS;

  const zip = await JSZip.loadAsync(blob);
  const rawPaths: string[] = [];
  zip.forEach((relPath, entry) => {
    if (entry.dir) return;
    rawPaths.push(relPath);
  });

  if (rawPaths.length === 0) {
    return { files: [], skipped: 0, rootPrefix: null };
  }

  const rootPrefix = detectRootPrefix(rawPaths);
  const stripped = rootPrefix
    ? rawPaths.map((p) => p.slice(rootPrefix.length)).filter(Boolean)
    : rawPaths.slice();

  let skipped = 0;
  const kept: string[] = [];
  for (const p of stripped) {
    if (SKIP_DIR_RE.test(p)) { skipped += 1; continue; }
    if (!isAllowed(p, allowedExts)) { skipped += 1; continue; }
    kept.push(p);
  }

  // Dedupe + sort for deterministic output (cartographer is order-sensitive).
  const unique = Array.from(new Set(kept)).sort();

  // Cap — if hit, prefer top-level files first (shorter paths win).
  const capped = unique.length > maxFiles
    ? [...unique].sort((a, b) => a.split('/').length - b.split('/').length || a.localeCompare(b)).slice(0, maxFiles).sort()
    : unique;

  if (unique.length > maxFiles) {
    skipped += unique.length - maxFiles;
  }

  return { files: capped, skipped, rootPrefix };
}

export const __zipScannerInternals = {
  DEFAULT_ALLOWED_EXTS,
  SPECIAL_BASENAMES,
  SKIP_DIR_RE,
  SKIP_FILE_RE,
  LOCKFILE_BASENAMES,
  detectRootPrefix,
  isAllowed,
};
